// Note: vue-clickaway is Vue 2 only. Replace with a custom directive or @vueuse/core onClickOutside
// import { directive as vClickOutside } from 'vue-clickaway'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import { IMaskDirective } from 'vue-imask'

/**
 * Convert old vue-the-mask string tokens to IMask pattern tokens.
 *   #  →  0   (any digit)
 *   A  →  a   (any letter)
 *   N  →  *   (any alphanumeric)
 *   X  →  *   (any character)
 * All other characters are treated as IMask literals (e.g. '/', '-').
 */
function toIMaskPattern (pattern) {
  return pattern.split('').map(ch => {
    if (ch === '#') return '0'
    if (ch === 'A') return 'a'
    if (ch === 'N' || ch === 'X') return '*'
    return ch
  }).join('')
}

/**
 * Normalise binding.value before passing to IMaskDirective:
 *  - String  → convert vue-the-mask tokens to IMask pattern, wrap as { mask }
 *  - Object  → pass through as-is (already IMask options)
 */
function normalizeOpts (value) {
  if (!value) return value
  if (typeof value === 'string') return { mask: toIMaskPattern(value) }
  return value
}

function withNormalizedBinding (binding) {
  return { ...binding, value: normalizeOpts(binding.value) }
}

/**
 * Wrap the official vue-imask IMaskDirective with two additions:
 *
 *  1. Native 'input' event on 'accept' — so Vue v-model picks up the
 *     formatted value without needing @accept in every template.
 *
 *  2. Focus guard in updated() — the official directive calls
 *     el.maskRef._onChange() whenever el.value !== el.maskRef.value.
 *     That call re-normalizes the field value, which drops a trailing
 *     decimal point (".") the user just typed before entering cents
 *     (e.g. "$1,234." → normalized back to "$1,234").
 *     We only call _onChange() when the field is NOT focused, so
 *     programmatic (Vuex/store) value changes still sync correctly
 *     while live typing is never interrupted.
 */
function withInputEvent (directive) {
  function patchEl (el) {
    if (el._maskInputPatched || !el.maskRef) return
    el._maskInputPatched = true
    el.maskRef.on('accept', () => {
      if (!el._maskFiring) {
        el._maskFiring = true
        el.dispatchEvent(new Event('input', { bubbles: true }))
        el._maskFiring = false
      }
    })
  }

  function destroyEl (el) {
    delete el._maskInputPatched
    delete el._maskFiring
  }

  return {
    beforeMount (el, binding, vnode, prev) {
      directive.beforeMount?.(el, withNormalizedBinding(binding), vnode, prev)
      patchEl(el)
      // IMask fires 'accept' during construction (inside directive.beforeMount above),
      // but our accept→input bridge from patchEl is registered AFTER that initial fire.
      // A pre-loaded value such as "5000" is therefore formatted to "$5,000.00" by
      // IMask but never propagated back to Vue's v-model — the raw number is shown
      // until the user focuses the field and triggers accept again.
      // Dispatch one synthetic input event here so v-model sees the formatted value.
      // NOTE: at beforeMount time el.value is usually still "" — see mounted()
      // below for why the real fix has to happen there. This early dispatch is
      // harmless and only helps in the (rare) case el.value is already set here.
      if (el.maskRef && el.value) {
        el._maskFiring = true
        el.dispatchEvent(new Event('input', { bubbles: true }))
        el._maskFiring = false
      }
    },

    mounted (el) {
      // Vue's built-in v-model (vModelText) does NOT set the initial value via
      // a "value" prop patched during mountElement (which runs before
      // directive beforeMount/mounted hooks). Instead it sets `el.value`
      // directly inside its OWN `mounted` hook — see runtime-dom's
      // `vModelText.mounted(el, { value }) { el.value = value }`.
      //
      // Directive hooks run in template-declaration order, and v-mask is
      // declared before v-model in every tax form template, so by the time
      // v-model's `mounted` hook runs, it overwrites whatever IMask formatted
      // in our beforeMount with the raw, unformatted value (e.g. "5000"
      // instead of "$5,000.00") — and nothing corrects it afterwards, since
      // there's no further hook in this same synchronous phase.
      //
      // Deferring to a microtask lets v-model's `mounted` hook finish first;
      // we then resync IMask from the (now raw) el.value using the `value`
      // setter, which reformats the display AND fires 'accept' (via
      // updateControl -> _fireChangeEvents), letting our accept→input bridge
      // in patchEl() sync the formatted value back into v-model.
      queueMicrotask(() => {
        if (el.maskRef && el.value !== el.maskRef.value) {
          el.maskRef.value = el.value
        }
      })
    },

    updated (el, binding, vnode, prev) {
      const nb = withNormalizedBinding(binding)
      const opts = nb.value
      if (opts) {
        if (el.maskRef) {
          el.maskRef.updateOptions(opts)
          if (el !== document.activeElement && el.value !== el.maskRef.value) {
            // el.maskRef._onChange() only re-syncs the internal masked model
            // from the raw DOM value — it does NOT reformat the displayed
            // value or fire the 'accept' event (see imask InputMask#_onChange,
            // which calls updateValue() but not updateControl()).
            // That meant a value set programmatically (e.g. Vuex-stored data
            // re-populated on $route re-init, reusing the same DOM node
            // because the v-for has no :key) rendered as raw text ("5000")
            // instead of the masked display ("$5,000.00") until the user
            // manually edited the field.
            // Using the `value` setter runs updateControl(), which reformats
            // el.value AND fires 'accept' (via _fireChangeEvents), letting
            // our accept→input bridge in patchEl() sync v-model correctly.
            el.maskRef.value = el.value
          }
        } else {
          directive.updated?.(el, nb, vnode, prev)
          patchEl(el)
          return
        }
      } else {
        directive.updated?.(el, nb, vnode, prev)
        destroyEl(el)
        return
      }
      patchEl(el)
    },

    unmounted (el, binding, vnode, prev) {
      directive.unmounted?.(el, withNormalizedBinding(binding), vnode, prev)
      destroyEl(el)
    }
  }
}

const GlobalDirectives = {
  install (app) {
    app.use(FloatingVue)
    // v-mask uses vue-imask's official IMaskDirective + v-model + cents fix
    app.directive('mask', withInputEvent(IMaskDirective))
  }
}

export default GlobalDirectives
